import { Component, EventEmitter, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ChannelsService } from '../channels.service';
import { ChannelFormData, FieldRule, LinkedSurvey, SkuRule } from '../channels.model';
import { ConfirmActionModalData } from '../../../shared/components/confirm-action-modal/confirm-action-modal.model';
import { HeaderAction } from '../../../shared/components/page-header/page-header.component';
import { CodeValidationState } from '../components/channel-identity-tab/channel-identity-tab.component';

export type ChannelTab = 'identity' | 'fieldRules' | 'surveys' | 'skuRules' | 'impactPreview';

@Component({
  selector: 'app-create-channel',
  standalone: false,
  templateUrl: './create-channel.component.html',
  styleUrl: './create-channel.component.scss'
})
export class CreateChannelComponent implements OnInit, OnDestroy {
  private channelsService = inject(ChannelsService);
  private router = inject(Router);
  private _subs: Subscription[] = [];

  readonly discardAction = new EventEmitter<void>();
  readonly createChannelAction = new EventEmitter<void>();

  activeTab: ChannelTab = 'identity';
  codeValidationState: CodeValidationState = 'idle';

  formData: ChannelFormData = { name: '', code: '', description: '', active: true };

  nameError = false;
  codeError = false;
  submitted = false;
  saving = false;
  creationSuccess = false;

  fieldRules: FieldRule[] = [
    { id: 'fr-1', fieldName: 'POS Name', alwaysRequired: true, priority: 'P0', enabled: true },
    { id: 'fr-2', fieldName: 'POS Code', alwaysRequired: true, conditionalLogic: ' unique', priority: 'P0', enabled: true },
    { id: 'fr-3', fieldName: 'Trade License ', priority: 'P1', alwaysRequired: true, enabled: true },
    { id: 'fr-4', fieldName: 'VAT Registration', priority: 'P1', alwaysRequired: true, enabled: true },
    { id: 'fr-5', fieldName: 'Owner contact', alwaysRequired: true, priority: 'P0', enabled: true },
  ];

  linkedSurveys: LinkedSurvey[] = [
    { id: 'sv-1', name: 'POS Onboarding Checklist', trigger: 'ON_CREATION', meta: '12 questions · v3.2 · last edited 8 Mar' },
    { id: 'sv-2', name: 'Quarterly Refresh', trigger: 'SCHEDULED', meta: 'every 90 days · 8 questions · v1.4' },
  ];

  availableSurveys: LinkedSurvey[] = [
    { id: 'sv-3', name: 'Quarterly Refresh Q2', trigger: 'SCHEDULED', meta: '8 questions · v1.5' },
    { id: 'sv-4', name: 'Product Launch Checklist', trigger: 'ON_CREATION', meta: '6 questions · v2.0' },
    { id: 'sv-5', name: 'Compliance Review', trigger: 'ON_UPDATE', meta: '10 questions · v1.1' },
  ];

  skuRules: SkuRule[] = [
    { id: 'sku-1', type: 'CATEGORY', code: 'CAT-T08', label: 'Tobacco — entire category', meta: '142 SKUs' },
    { id: 'sku-2', type: 'ALLOW', code: 'SKU-IQOS-V3', label: 'IQOS Iluma One — Standard pack', meta: 'since 8 Jan' },
    { id: 'sku-3', type: 'ALLOW', code: 'SKU-TER-3', label: 'TEREA Amber — 20-stick pack', meta: 'since 14 Feb' },
  ];

  showConfirmModal = false;
  confirmModalData!: ConfirmActionModalData;

  get headerTitle(): string { return this.formData.name.trim() || 'New channel'; }
  get codePreview(): string { return this.formData.code || 'CH-???'; }
  get allowedSkuCount(): number { return this.skuRules.filter(r => r.type !== 'RESTRICT').length; }
  get restrictedSkuCount(): number { return this.skuRules.filter(r => r.type === 'RESTRICT').length; }

  get totalConfigItems(): number {
    return this.fieldRules.length + this.linkedSurveys.length + this.skuRules.length;
  }

  get headerBadge(): string {
    if (!this.formData.name) return 'Draft';
    return this.formData.active ? 'Active' : 'Inactive';
  }

  get headerActions(): HeaderAction[] {
    return [
      { label: 'Discard', variant: 'secondary', disabled: this.saving },
      { label: 'Create channel', variant: 'primary', disabled: this.saving || this.creationSuccess }
    ];
  }

  onHeaderAction(action: HeaderAction): void {
    if (action.label === 'Discard') this.onDiscard();
    else if (action.label === 'Create channel') this.tryCreate();
  }

  ngOnInit(): void {
    this._subs.push(
      this.discardAction.subscribe(() => this.onDiscard()),
      this.createChannelAction.subscribe(() => this.tryCreate()),
    );
  }

  ngOnDestroy(): void { this._subs.forEach(s => s.unsubscribe()); }

  setActiveTab(tab: ChannelTab): void { this.activeTab = tab; }

  onDiscard(): void { this.router.navigate(['/channels']); }

  tryCreate(): void {
    this.submitted = true;
    this.nameError = !this.formData.name.trim();
    this.codeError = !this.formData.code.trim() || this.codeValidationState === 'taken';

    if (this.nameError || this.codeError) {
      this.setActiveTab('identity');
      return;
    }

    this.confirmModalData = {
      icon: 'warning',
      title: `Create channel "${this.formData.name}"?`,
      description: `**${this.totalConfigItems} configuration items** across 3 engines will be applied. The channel code **${this.formData.code}** is permanent and cannot be changed after creation.`,
      effects: [
        { type: 'positive', text: 'Channel is immediately available for POS assignment' },
        { type: 'positive', text: `${this.fieldRules.length} mandatory field rule${this.fieldRules.length !== 1 ? 's' : ''} will be enforced on all new POS` },
        { type: 'positive', text: `${this.linkedSurveys.length} survey${this.linkedSurveys.length !== 1 ? 's' : ''} will be triggered on creation` },
        { type: 'positive', text: 'Reversible within 24 hours via audit log' },
      ],
      confirmation: {
        label: this.formData.code,
        expectedValue: this.formData.code,
        placeholder: this.formData.code,
      },
      actions: [{
        label: 'Create channel',
        style: 'primary',
        icon: 'arrow-right',
        action: 'confirm',
        disabledUntilConfirmed: true
      }]
    };
    this.showConfirmModal = true;
  }

  onModalAction(action: string): void {
    if (action === 'confirm') {
      this.showConfirmModal = false;
      this.doCreate();
    }
  }

  onModalDismiss(): void { this.showConfirmModal = false; }

  private doCreate(): void {
    this.saving = true;
    this.channelsService.create({
      name: this.formData.name,
      code: this.formData.code,
      description: this.formData.description || undefined,
      active: this.formData.active
    }).subscribe({
      next: () => { this.saving = false; this.creationSuccess = true; },
      error: (err) => { this.saving = false; console.error('Failed to create channel:', err); }
    });
  }

  redirectToList(): void { this.router.navigate(['/channels']); }
}
